import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { Utils } from '../../shared/classes/utils';
import { AbmTargetList, Country, AccountArea, PageTag } from '../../shared/models/models';
import { AccountContextService } from '../../shared/services/account-context.service';
import { AccountEntitiesService } from '../../shared/services/account-entities.service';
import { DeviceRulesFields, driftEvents, IntegrationRulesFields, pageRulesFields, VisitorRulesFields } from '../experience/experience-configs';
import { CompaniesRevenues, CompaniesSizes, Industries } from '../insights/audience-rules-models';
import { IntegrationState, IntegrationStatus, MaField, MaList } from '../models/integrations';
import { CampaignsAttributes, SOURCES_TYPES_MAP } from '../models/models';
import { SettingsService } from '../settings/account-settings.service';
import { integrations } from '../settings/integrations/integrations';
import { IntegrationsService } from '../settings/integrations/integrations.service';
import { AudiencesService } from './audiences.service';
import { ExperienceService } from './experience.service';
import { GoalsService } from './goals.service';
import { InsightsService } from './insights.service';
import { PersonalizationService } from './personalization.service';
import { AbmService } from '../abm/abm.service';
import { GoalSettings } from '../models/goals';
import { SettingsDomainResponse } from '../settings/models/settings-models';
import { IListResponse } from '../../shared/models/IListResponse';
import { ExperienceUnitListItem, SurveyQuestionForAudience } from '../experience/models';
import { comparisonInputType, comparisonValueType, predicateGroupName, Rule, RuleField, RuleOperatorConfig, RuleOperatorInvalidFieldType, ruleOperatorType, RuleOptionsData, RuleOptionsDataValue, RulesConfig, RulesOptions, RuleValidationError } from '../../shared/models/rules.models';

@Injectable({
  providedIn: 'root'
})
export class RulesService {
  protected accountContext = inject(AccountContextService);
  protected goalsService = inject(GoalsService);
  protected entitiesService = inject(AccountEntitiesService);
  protected experienceService = inject(ExperienceService);
  protected insightsService = inject(InsightsService);
  protected settingsService = inject(SettingsService);
  protected integrationsService = inject(IntegrationsService);
  protected personalizationService = inject(PersonalizationService);
  protected audiencesService = inject(AudiencesService);
  protected abmService = inject(AbmService);

  activeIntegration: IntegrationStatus;
  visitorRulesFields = VisitorRulesFields;
  urlRulesFields = pageRulesFields;
  deviceRulesFields = DeviceRulesFields;
  integrationRulesFields: RuleField[];
  rulesPageOptions: RulesOptions;
  rulesVisitorsOptions: RulesOptions;
  rulesIntegrationOptions: RulesOptions;

  getOperators(operatorType: ruleOperatorType): RuleOperatorConfig[] {
    switch (operatorType) {
      case ruleOperatorType.ruleIntGreaterThanOperators: return this.ruleIntGreaterThanOperators;
      case ruleOperatorType.ruleIntOperators: return this.ruleIntOperators;
      case ruleOperatorType.ruleIntOperatorsFull: return this.ruleIntOperatorsFull;
      case ruleOperatorType.ruleStringOperators: return this.ruleStringOperators;
      case ruleOperatorType.ruleStringOperatorsFull: return this.ruleStringOperatorsFull;
      case ruleOperatorType.ruleBooleanOperators: return this.ruleBooleanOperators;
      case ruleOperatorType.ruleCollectionOperators: return this.ruleCollectionOperators;
      case ruleOperatorType.ruleStringFromListOperators: return this.ruleStringFromListOperators;
      case ruleOperatorType.ruleSelectOneOperators: return this.ruleSelectOneOperators;
      case ruleOperatorType.ruleCollectionOperatorsReduced: return this.ruleCollectionOperatorsReduced;
      case ruleOperatorType.ruleCollectionOperatorContainsAny: return this.ruleCollectionOperatorContainsAny;
      case ruleOperatorType.ruleStringFromListOperatorsInclude: return this.ruleStringFromListOperatorsInclude;
      case ruleOperatorType.ruleCollectionOperatorsInclElementContains: return this.ruleCollectionOperatorsInclElementContains;
      case ruleOperatorType.ruleStringOperatorsForQuery: return this.ruleStringOperatorsForQuery;
      case ruleOperatorType.ruleStringOperatorsReduced: return this.ruleStringOperatorsReduced;
      case ruleOperatorType.ruleStringOperatorsAceTargeting: return this.ruleStringOperatorsAceTargeting;
      case ruleOperatorType.ruleCollectionPositive: return this.ruleCollectionPositive;
      default: return null;
    }
  }

  async loadTargetingCollections(excludeUnitId?: number, isExcludeMaIntegrations = false): Promise<void> {
    const [goalsRes, statesRes, campaignsAttibutes, countriesRes, tagsSettingsRes, areasRes, unitsRes,
      domainsRes, sixSenseSegments, targetLists, audiencesRes] =
      await Promise.all([
        lastValueFrom(this.goalsService.getGoals({ status: 'active' })),
        lastValueFrom(this.entitiesService.getStates()),
        lastValueFrom(this.insightsService.getAccountCampaignsAttibutes()),
        lastValueFrom(this.entitiesService.getCountries()),
        lastValueFrom(this.personalizationService.getAllPagesTags()),
        lastValueFrom(this.settingsService.getAreas()),
        lastValueFrom(this.experienceService.getAllUnits({ status: 'active' })),
        lastValueFrom(this.settingsService.getDomains()),
        lastValueFrom(this.settingsService.getClientAppItems('sixsense')),
        lastValueFrom(this.entitiesService.getAccountTargetLists()),
        lastValueFrom(this.audiencesService.getAudiences()),
      ]);

    let domains: string[];
    if (domainsRes.domains.length > 0) {
      domains = [domainsRes.siteDomain, ...domainsRes.domains.map(d => d.domain)].map(d => d.replace('https://', ''));
    }

    this.rulesPageOptions = {
      config: {},
      data: {
        [comparisonValueType.tags_collection]: tagsSettingsRes?.map(x => ({ id: x.id.toString(), name: x.name })),
        [comparisonValueType.areas_collection]: areasRes.areas?.map(x => ({ id: x.id.toString(), name: x.name })),
        [comparisonValueType.domains_collection]: domains
      }
    };

    const units = unitsRes.items
      .filter(unit => (unit.unitId !== excludeUnitId && unit.uiType !== 'script'))
      .map(x => ({ id: x.unitId, name: x.name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const surveyQuestionsRes = await lastValueFrom(this.experienceService.getAllSurveyQuestions());
    const surveyQuestions = Utils.groupObjectsByProperty(
      surveyQuestionsRes
        .filter(unit => (unit.surveyId !== excludeUnitId))
        .map(q => ({ id: q.key, name: q.fullName, group: q.surveyName })),
      'group');
    if (Object.keys(surveyQuestions).length === 0) {
      this.visitorRulesFields = this.visitorRulesFields.filter(r => r.predicate !== 'SurveyAnswers');
    }

    const sourceTypesCollection = Object.entries(SOURCES_TYPES_MAP)
      .map(([id, name]) => ({ id: id.toString(), name: Utils.capitalizeFirst(name) }));

    this.rulesVisitorsOptions = {
      config: {},
      data: {
        [comparisonValueType.events_and_goals_collection]: goalsRes.data,
        [comparisonValueType.referrers_collection]: campaignsAttibutes.referrals.map(x => ({ id: x, name: x })),
        [comparisonValueType.campaigns_collection]: campaignsAttibutes.utm_campaigns,
        [comparisonValueType.sources_collection]: campaignsAttibutes.utm_sources.map(x => ({ id: x, name: x })),
        [comparisonValueType.source_types_collection]: sourceTypesCollection,
        [comparisonValueType.areas_collection]: areasRes.areas?.map(x => ({ id: x.id.toString(), name: x.name })),
        [comparisonValueType.country_iso_collection]: countriesRes.map(c => ({ id: c.code_country, name: c.label })),
        [comparisonValueType.region_iso_collection]: statesRes,
        [comparisonValueType.industries_collection]: Industries.map(i => ({ id: i, name: i })),
        [comparisonValueType.units_collection]: units,
        [comparisonValueType.target_lists]: targetLists.data.map(x => ({ id: x.list_id, name: x.list_name })),
        [comparisonValueType.question_collection]: surveyQuestions,
        [comparisonValueType.company_sizes_collection]: CompaniesSizes,
        [comparisonValueType.company_revenues_collection]: CompaniesRevenues,
        [comparisonValueType.sixsense_segments]: sixSenseSegments,
        [comparisonValueType.tags_collection]: tagsSettingsRes?.map(x => ({ id: x.id.toString(), name: x.name })),
        [comparisonValueType.audiences_collection]: audiencesRes.data.map(x => ({ id: x.id.toString(), name: x.name })),
        [comparisonValueType.empty_collection]: [],
      }
    };

    if (!isExcludeMaIntegrations) {
      this.activeIntegration = await this.getActiveIntegrations();
      if (this.activeIntegration) {
        await this.loadIntegrationCollections(this.activeIntegration.integrationId);
      }
    }
  }

  async getRulesData(valueTypes: comparisonValueType[]): Promise<RuleOptionsData> {
    let campaignsAttributesIdx: number | null = null;
    const dataPromiseList = valueTypes.map((vType, idx) => {
      switch (vType) {
        case comparisonValueType.country_iso_collection: return lastValueFrom(this.entitiesService.getCountries());
        case comparisonValueType.region_iso_collection: return lastValueFrom(this.entitiesService.getStates());
        case comparisonValueType.company_sizes_collection: return Promise.resolve(CompaniesSizes);
        case comparisonValueType.company_revenues_collection: return Promise.resolve(CompaniesRevenues);
        case comparisonValueType.industries_collection: return Promise.resolve(Industries);
        case comparisonValueType.drift_events_collection: return Promise.resolve(driftEvents);
        case comparisonValueType.target_lists: return lastValueFrom(this.entitiesService.getAccountTargetLists());
        case comparisonValueType.sixsense_segments: return lastValueFrom(this.settingsService.getClientAppItems('sixsense'));
        case comparisonValueType.areas_collection: return this.entitiesService.getAccountAreas();
        case comparisonValueType.tags_collection: return lastValueFrom(this.personalizationService.getAllPagesTags());
        case comparisonValueType.events_and_goals_collection: return lastValueFrom(this.goalsService.getGoals({ status: 'active' }));
        case comparisonValueType.deals_stages: return lastValueFrom(this.abmService.dealStages());
        case comparisonValueType.units_collection: return lastValueFrom(this.experienceService.getAllUnits({ status: 'active' }));
        case comparisonValueType.domains_collection: return lastValueFrom(this.settingsService.getDomains());
        case comparisonValueType.referrers_collection:
        case comparisonValueType.campaigns_collection:
        case comparisonValueType.sources_collection:
          if (campaignsAttributesIdx === null) {
            campaignsAttributesIdx = idx;
            return lastValueFrom(this.insightsService.getAccountCampaignsAttibutes());
          } else {
            return Promise.resolve(null);
          }
        case comparisonValueType.question_collection:
          return lastValueFrom(this.experienceService.getAllSurveyQuestions());
        case comparisonValueType.empty_collection: return Promise.resolve([]);
        // need to add additional rules data as needed
        default: return Promise.resolve(null);
      }
    });
    const dataResults = await Promise.all(dataPromiseList);
    const entries: [comparisonValueType, RuleOptionsDataValue][] = valueTypes.map((vType, i) => {
      let value: RuleOptionsDataValue;
      switch (vType) {
        case comparisonValueType.country_iso_collection: value = dataResults[i].map((c: Country) => ({ id: c.code_country, name: c.label })); break;
        case comparisonValueType.industries_collection: value = dataResults[i].map((i: string) => ({ id: i, name: i })); break;
        case comparisonValueType.target_lists: value = dataResults[i].data.map((x: AbmTargetList) => ({ id: x.list_id, name: x.list_name })); break;
        case comparisonValueType.areas_collection: value = dataResults[i].data.map((x: AccountArea) => ({ id: x.areaId.toString(), name: x.name })); break;
        case comparisonValueType.tags_collection: value = dataResults[i]?.map((x: PageTag) => ({ id: x.id.toString(), name: x.name })); break;
        case comparisonValueType.events_and_goals_collection: value = dataResults[i].data.map((x: GoalSettings) => ({ id: x.id.toString(), name: x.name })); break;
        case comparisonValueType.deals_stages: value = dataResults[i].map((i: string) => ({ id: i, name: i })); break;
        case comparisonValueType.units_collection:
          value = (dataResults[i] as IListResponse<ExperienceUnitListItem>).items
            .filter(unit => (unit.uiType !== 'script'))
            .map(x => ({ id: x.unitId, name: x.name }))
            .sort((a, b) => a.name.localeCompare(b.name));
          break;
        case comparisonValueType.domains_collection:
          const domainRes: SettingsDomainResponse = dataResults[i];
          value = [domainRes.siteDomain, ...domainRes.domains.map(d => d.domain)]
            .map(d => d.replace('https://', ''));
          break;
        case comparisonValueType.referrers_collection: value = (dataResults[campaignsAttributesIdx] as CampaignsAttributes).referrals.map(x => ({ id: x, name: x })); break
        case comparisonValueType.campaigns_collection: value = (dataResults[campaignsAttributesIdx] as CampaignsAttributes).utm_campaigns; break
        case comparisonValueType.sources_collection: value = (dataResults[campaignsAttributesIdx] as CampaignsAttributes).utm_sources.map(x => ({ id: x, name: x })); break
        case comparisonValueType.source_types_collection:
          value = Object.entries(SOURCES_TYPES_MAP)
            .map(([id, name]) => ({ id: id.toString(), name: Utils.capitalizeFirst(name) }));
          break;
        case comparisonValueType.question_collection:
          value = dataResults[i].length === 0
            ? []
            : Utils.groupObjectsByProperty(dataResults[i].map((q: SurveyQuestionForAudience) => ({ id: q.key, name: q.fullName, group: q.surveyName })), 'group');
          break;
        default: value = dataResults[i]; break;
      }
      return [vType, value]
    });
    return Object.fromEntries(entries);
  }

  async getActiveIntegrations(): Promise<IntegrationStatus> {
    const activeIntegrations = await lastValueFrom(this.integrationsService.getActiveIntegrations());
    return activeIntegrations?.find(e => (
      e.status === IntegrationState.online && e.connectionStatus.code === 0
      && integrations.filter(i => i.id === e.integrationId && i.experienceAvailable === true).length > 0)
    );
  }

  async loadIntegrationCollections(integrationId: number): Promise<void> {
    let lists: MaList[] = [];
    try {
      lists = await lastValueFrom(this.integrationsService.getIntegrationsLists(integrationId));
    } catch {
      console.error('Failed to get MA lists');
    }

    this.rulesIntegrationOptions = {
      config: {},
      data: {
        [comparisonValueType.malists_collection]: lists
      }
    };

    let fields: MaField[] = [];
    try {
      fields = await lastValueFrom(this.integrationsService.getIntegrationsFields(integrationId));
    } catch {
      console.error('Failed to get Ma fields');
    }

    const rulesFields = fields.map((field) => {
      let fieldType: comparisonInputType;
      let operatorType: ruleOperatorType;
      switch (field.fieldType) {
        case 'integer':
        case 'float':
        case 'currency':
        case 'number': {
          fieldType = comparisonInputType.number;
          operatorType = ruleOperatorType.ruleIntOperators;
          break;
        }
        case 'date':
        case 'datetime': {
          fieldType = comparisonInputType.dateTime;
          operatorType = ruleOperatorType.ruleIntOperators;
          break;
        }
        default: {
          fieldType = comparisonInputType.textField;
          operatorType = ruleOperatorType.ruleStringOperatorsFull;
        }
      }

      let description: string;
      if (field.description !== field.label) {
        description = field.description;
      } else {
        description = '';
      }

      const ruleFieldLine = {
        name: field.label,
        predicate: `Properties[${field.name}]`,
        valueType: comparisonValueType.string,
        isDaysPredicate: false,
        fieldType,
        operatorType,
        group: predicateGroupName.maFields,
        description
      };
      return ruleFieldLine;
    });

    this.integrationRulesFields = [...IntegrationRulesFields, ...rulesFields];
  }

  getRulesConfigErrors(rulesConfig: RulesConfig, ruleFields: RuleField[]): RuleValidationError[] {
    return rulesConfig.rulesGroups
      .flatMap(g => g.rules)
      .map(r => this.getRuleError(r, ruleFields))
      .filter(e => e !== null);
  }

  private getRuleError(rule: Rule, ruleFields: RuleField[]): RuleValidationError {
    let field = ruleFields.find(f => f.predicate === rule.comparisonPredicate);
    if (!field) {
      // field not found in simple comparison - checking for last days field
      const lastDaysMatch = rule.comparisonPredicate.match(/^([a-zA-Z]+)Last(7|30|90)Days$/);
      if (lastDaysMatch && lastDaysMatch.length === 3) {
        const lastDaysPredicate = `${lastDaysMatch[1]}Last{days}Days`;
        field = ruleFields.find(f => f.predicate === lastDaysPredicate);
      }
      if (!field) {
        return {
          rule: rule,
          invalidFieldType: RuleOperatorInvalidFieldType.Predicate,
          invalidFieldValue: rule.comparisonPredicate
        }
      }
    }

    const allowedOperators = this.getOperators(field.operatorType);
    const operator = allowedOperators.find(o => o.name === rule.comparisonOperator);
    if (!operator) {
      return {
        rule: rule,
        invalidFieldType: RuleOperatorInvalidFieldType.Operator,
        invalidFieldValue: rule.comparisonOperator
      }
    }

    if (field.predicate === 'Details.Industry' && this.getInvalidIndustries(rule.comparisonValue).length > 0) {
      return {
        rule: rule,
        invalidFieldType: RuleOperatorInvalidFieldType.Value,
        invalidFieldValue: rule.comparisonValue
      }
    }

    return null;
  }

  getInvalidIndustries(industryValue: string): string[] {
    const industries = industryValue.split('|');
    return industries.filter(i => !Industries.includes(i))
  }
  private ruleCollectionOperators: RuleOperatorConfig[] = [
    { name: 'CollectionContainsAnyOf', label: 'Contains Any' },
    { name: 'CollectionNotContainsAnyOf', label: 'Not Contains Any' },
    { name: 'CollectionContainsAll', label: 'Contains All' }
  ];

  private ruleCollectionOperatorsInclElementContains: RuleOperatorConfig[] = [
    { name: 'CollectionContainsAnyOf', label: 'Contains Any' },
    { name: 'CollectionNotContainsAnyOf', label: 'Not Contains Any' },
    { name: 'CollectionContainsAll', label: 'Contains All' },
    { name: 'CollectionElementContains', label: 'Contains Text' }
  ];

  private ruleCollectionPositive: RuleOperatorConfig[] = [
    { name: 'CollectionElementContains', label: 'Contains' },
    { name: 'CollectionElementStartsWith', label: 'Starts With' },
    { name: 'CollectionElementEndsWith', label: 'Ends With' }
  ];

  private ruleCollectionOperatorsReduced: RuleOperatorConfig[] = [
    { name: 'CollectionContainsAnyOf', label: 'Contains Any' },
    { name: 'CollectionContainsAll', label: 'Contains All' }
  ];

  private ruleCollectionOperatorContainsAny: RuleOperatorConfig[] = [
    { name: 'CollectionContainsAnyOf', label: 'Contains Any' }
  ];

  private ruleIntGreaterThanOperators: RuleOperatorConfig[] = [
    { name: 'GreaterThan', label: 'Greater Than' },
    { name: 'GreaterThanOrEqual', label: 'Greater Than Or Equal' },
  ];

  private ruleIntOperators: RuleOperatorConfig[] = [
    { name: 'Equal', label: 'Equal' },
    { name: 'NotEqual', label: 'Not Equal' },
    { name: 'GreaterThan', label: 'Greater Than' },
    { name: 'GreaterThanOrEqual', label: 'Greater Than Or Equal' },
    { name: 'LessThan', label: 'Less Than' },
    { name: 'LessThanOrEqual', label: 'Less Than Or Equal' }
  ];

  private ruleStringOperatorsForQuery: RuleOperatorConfig[] = [
    { name: 'StringEqualsCaseInsensitive', label: 'Equal' },
    { name: 'StringNotEqualsCaseInsensitive', label: 'Not Equal' },
    { name: 'StringStartsWith', label: 'Starts with' },
    { name: 'StringEndsWith', label: 'Ends with' },
    { name: 'StringContains', label: 'Contains' },
    { name: 'StringNotContains', label: 'Not Contains' }
  ];

  private ruleStringOperators: RuleOperatorConfig[] = [
    { name: 'StringEqualsCaseInsensitive', label: 'Equal' },
    { name: 'StringNotEqualsCaseInsensitive', label: 'Not Equal' },
    { name: 'StringStartsWith', label: 'Starts with' },
    { name: 'StringEndsWith', label: 'Ends with' },
    { name: 'StringContains', label: 'Contains' },
    { name: 'StringNotContains', label: 'Not Contains' },
    { name: 'StringMatchesRegex', label: 'Matches Regular expression' }
  ];

  private ruleStringOperatorsFull: RuleOperatorConfig[] = [
    ...this.ruleStringOperators,
    { name: 'StringNullOrEmpty', label: 'Is Empty' },
    { name: 'StringNotNullOrEmpty', label: 'Not Empty' },
  ];

  private ruleStringOperatorsReduced: RuleOperatorConfig[] = [
    { name: 'StringEqualsCaseInsensitive', label: 'Equal' },
    { name: 'StringStartsWith', label: 'Starts with' },
    { name: 'StringEndsWith', label: 'Ends with' },
    { name: 'StringContains', label: 'Contains' },
    { name: 'StringMatchesRegex', label: 'Matches Regular expression' }
  ];

  private ruleStringOperatorsAceTargeting: RuleOperatorConfig[] = [
    { name: 'StringStartsWith', label: 'Starts with' },
    { name: 'StringEndsWith', label: 'Ends with' },
    { name: 'StringContains', label: 'Contains' }
  ];

  private ruleIntOperatorsFull: RuleOperatorConfig[] = [
    ...this.ruleIntOperators,
    { name: 'StringNullOrEmpty', label: 'Is Empty' },
    { name: 'StringNotNullOrEmpty', label: 'Not Empty' },
  ];

  private ruleStringFromListOperators: RuleOperatorConfig[] = [
    { name: 'In', label: 'Includes' },
    { name: 'NotIn', label: 'Does not include' }
  ];

  private ruleStringFromListOperatorsInclude: RuleOperatorConfig[] = [
    { name: 'In', label: 'Includes' }
  ];

  private ruleSelectOneOperators: RuleOperatorConfig[] = [
    { name: 'Equal', label: 'Equal' },
    { name: 'NotEqual', label: 'Not Equal' },
  ];

  private ruleBooleanOperators: RuleOperatorConfig[] = [
    { name: 'IsTrue', label: 'True' },
    { name: 'IsFalse', label: 'False' }
  ];
}
